import React from "react";

export default function RefundPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 text-gray-300">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Refund Policy</h1>
        <p className="text-gray-400 text-sm">Effective Date: June 15, 2026 &nbsp;|&nbsp; Last Updated: June 15, 2026</p>
        <div className="h-1 w-24 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full mx-auto mt-4" />
      </div>

      <div className="space-y-10 text-sm md:text-base leading-relaxed">

        {/* Philosophy */}
        <section className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-3">Our Philosophy</h2>
          <p className="text-lg text-gray-200 italic">
            "We don't want your money if you're not getting value. It's that simple."
          </p>
          <p className="mt-3 text-gray-400">
            At Kronos AI, we believe in building products people actually want to use. If you're not satisfied, we'd rather fix the problem or give you your money back than keep a customer who isn't happy. Unhappy customers don't build great companies.
          </p>
        </section>

        {/* The Simple Version */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">1.</span> The Simple Version
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-lg p-5">
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-green-400 text-xl">✓</span>
                <span><strong className="text-white">First 7 days:</strong> Full refund, no questions asked. Try it, break it, test it. If it's not for you, just ask.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-green-400 text-xl">✓</span>
                <span><strong className="text-white">Technical issues:</strong> If our system fails and you can't use what you paid for, we'll refund or credit you. That's on us.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellow-400 text-xl">~</span>
                <span><strong className="text-white">After 7 days:</strong> Partial refund based on usage. We're fair, you be fair.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-400 text-xl">✗</span>
                <span><strong className="text-white">Abuse:</strong> If you use the service extensively and then request a refund, that's not cool. We reserve the right to decline.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Free Trial */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">2.</span> Free Tier = Zero Risk
          </h2>
          <p>We offer a <strong className="text-white">free tier forever</strong>. Use it. Test the product. See if it solves your problem. Only upgrade when you're confident it's worth it.</p>
          <p className="mt-3 text-gray-400">This isn't a 14-day trial that expires. It's a permanent free tier with real functionality. We'd rather you start free and upgrade because you love it than start paid and request a refund because you didn't.</p>
        </section>

        {/* Subscription Refunds */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">3.</span> Subscription Refunds
          </h2>

          <h3 className="text-white font-semibold mb-2 mt-4">Monthly Plans</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left px-4 py-3 text-white font-semibold">Timing</th>
                  <th className="text-left px-4 py-3 text-white font-semibold">Refund</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                <tr><td className="px-4 py-3">Within 7 days</td><td className="px-4 py-3 text-green-400">100% refund</td></tr>
                <tr><td className="px-4 py-3">Days 8-14</td><td className="px-4 py-3 text-yellow-400">50% refund</td></tr>
                <tr><td className="px-4 py-3">After 14 days</td><td className="px-4 py-3 text-gray-400">No refund (cancel anytime for next cycle)</td></tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-white font-semibold mb-2 mt-6">Annual Plans</h3>
          <p>Annual plans come with a significant discount. In exchange, we ask for commitment.</p>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm border border-white/10 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left px-4 py-3 text-white font-semibold">Timing</th>
                  <th className="text-left px-4 py-3 text-white font-semibold">Refund</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                <tr><td className="px-4 py-3">Within 14 days</td><td className="px-4 py-3 text-green-400">100% refund</td></tr>
                <tr><td className="px-4 py-3">Within 30 days</td><td className="px-4 py-3 text-yellow-400">Pro-rated refund minus one month at regular price</td></tr>
                <tr><td className="px-4 py-3">After 30 days</td><td className="px-4 py-3 text-gray-400">No refund (you can still cancel, service continues until expiry)</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* What We Won't Refund */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">4.</span> What We Won't Refund
          </h2>
          <p className="mb-3">We're fair, but we're not naive. No refunds for:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li><strong className="text-white">"I forgot to cancel"</strong> — We send reminder emails before renewal. Set a calendar reminder.</li>
            <li><strong className="text-white">Heavy usage then refund request</strong> — If you've generated 500 proposals or made 100 AI calls, you got value. That's not refundable.</li>
            <li><strong className="text-white">Violation of Terms</strong> — If your account was suspended for abuse, spam, or ToS violations.</li>
            <li><strong className="text-white">Third-party issues</strong> — If Twitter/Facebook changes their API or your social account gets banned, that's not on us.</li>
            <li><strong className="text-white">"Changed my mind" after 30 days</strong> — A month is enough time to know if something works for you.</li>
          </ul>
        </section>

        {/* How to Request */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">5.</span> How to Request a Refund
          </h2>
          <div className="bg-white/5 border border-white/10 rounded-lg p-5">
            <ol className="space-y-3 list-decimal list-inside">
              <li>Email <a href="mailto:kronos@devxhouse.com" className="text-cyan-400 hover:underline">kronos@devxhouse.com</a> with subject: "Refund Request"</li>
              <li>Include your account email and reason (helps us improve)</li>
              <li>We'll respond within 24-48 hours</li>
              <li>If approved, refund processes within 5-10 business days via Paddle</li>
            </ol>
          </div>
          <p className="mt-4 text-gray-400">Don't write a novel. Don't beg. Just tell us what happened. We're humans, we get it.</p>
        </section>

        {/* Service Credits */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">6.</span> Service Credits (Alternative to Refund)
          </h2>
          <p>Sometimes a refund isn't what you actually need. If there was a technical issue or you had a bad experience, we might offer:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300 mt-3">
            <li>Extended subscription time</li>
            <li>Upgrade to a higher tier for the remaining period</li>
            <li>Additional AI credits or usage limits</li>
          </ul>
          <p className="mt-3 text-gray-400">Often this is a better deal for you than a refund. Just ask.</p>
        </section>

        {/* Enterprise */}
        <section>
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <span className="text-cyan-400">7.</span> Enterprise Customers
          </h2>
          <p>Enterprise plans have custom terms negotiated in your contract. This policy doesn't override your specific agreement. Contact your account manager directly.</p>
        </section>

        {/* Chargebacks */}
        <section className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-3">A Note on Chargebacks</h2>
          <p>Filing a chargeback with your bank without contacting us first is not cool. It costs us fees, creates paperwork, and damages trust.</p>
          <p className="mt-3"><strong className="text-white">If you have a legitimate issue, talk to us first.</strong> We're reasonable people. Chargebacks filed without prior contact will result in permanent account ban and we will dispute them with documentation of service delivery.</p>
        </section>

        {/* Final Note */}
        <section className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-3">Final Thought</h2>
          <p className="text-lg text-gray-200">
            We're building Kronos AI because we believe AI should make business easier, not harder. If we're not delivering on that promise for you, we'd rather give you your money back and part ways respectfully than keep a customer who feels cheated.
          </p>
          <p className="mt-4 text-gray-400">
            That said, we're a small team working hard to build something great. If you're happy with the product, a kind review means more to us than you might think.
          </p>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-xl font-bold text-white mb-4">Questions?</h2>
          <div className="space-y-2">
            <p>📧 <a href="mailto:kronos@devxhouse.com" className="text-cyan-400 hover:underline">kronos@devxhouse.com</a></p>
            <p className="text-gray-400 text-sm">Response time: Usually within 24 hours. We actually read these.</p>
          </div>
        </section>

        <p className="text-center text-gray-500 text-sm pt-4 border-t border-white/10">
          This Refund Policy was last updated on June 15, 2026.
        </p>
      </div>
    </div>
  );
}
